<script lang="ts">
  import { SPONSORBLOCK_ATTRIBUTION } from '../player/sponsorblock';
  import { connectionHeadline } from '../room/client';
  import type { RoomState } from '../room/state.svelte';
  import ChatPanel from '../social/ChatPanel.svelte';
  import ReactionBar from '../social/ReactionBar.svelte';
  import { checkUsername, saveUsername, USERNAME_MAX_LENGTH } from '../session/username';
  import ParticipantsPane from './ParticipantsPane.svelte';
  import PlayerPane from './PlayerPane.svelte';
  import QueuePane from './QueuePane.svelte';
  import SettingsPane from './SettingsPane.svelte';
  import { SEEK_STEP_MS, SHORTCUT_HELP, shortcutFor } from './shortcuts';

  const { room, onLeave }: { room: RoomState; onLeave: () => void } = $props();

  let renaming = $state(false);
  /** `null` until edited, so the room's current name stays the default. */
  let nameEdit = $state<string | null>(null);
  let nameError = $state<string | null>(null);
  let helpOpen = $state(false);
  let nameInput = $state<HTMLInputElement | null>(null);

  const nameValue = $derived(nameEdit ?? room.desiredUsername);
  const you = $derived(room.you);
  const permissions = $derived(room.permissions);
  const connection = $derived(room.connection);

  $effect(() => {
    if (!renaming) return;
    nameInput?.focus();
    // Select the current name: a rename is usually a replacement.
    nameInput?.select();
  });

  async function submitRename(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const checked = checkUsername(nameValue);
    if (!checked.ok) {
      nameError = checked.message;
      return;
    }
    nameError = null;
    // The preference is saved locally even if the room rejects the rename;
    // it is a browser preference, not room state.
    saveUsername(checked.username);
    const outcome = await room.setUsername(checked.username);
    if (outcome.result === 'ok') {
      renaming = false;
      nameEdit = null;
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    const action = shortcutFor(event);
    if (action === null) return;

    const player = room.player;
    switch (action) {
      case 'toggle-play': {
        event.preventDefault();
        void room.run({ type: room.playback.state === 'playing' ? 'pause' : 'play' });
        return;
      }
      case 'seek-back':
      case 'seek-forward': {
        event.preventDefault();
        if (player.durationMs === 0) return;
        const delta = action === 'seek-forward' ? SEEK_STEP_MS : -SEEK_STEP_MS;
        const target = Math.min(
          Math.max(player.positionMs + delta, 0),
          player.durationMs,
        );
        void room.run({ type: 'seek', position_ms: Math.round(target) });
        return;
      }
      case 'toggle-mute': {
        event.preventDefault();
        player.setMuted(!player.muted);
        return;
      }
      case 'toggle-fullscreen': {
        event.preventDefault();
        void player.toggleFullscreen();
        return;
      }
      case 'toggle-captions': {
        event.preventDefault();
        if (player.subtitles.length === 0) return;
        player.selectSubtitle(player.activeSubtitle === null ? 0 : null);
        return;
      }
      case 'toggle-help': {
        event.preventDefault();
        helpOpen = !helpOpen;
        return;
      }
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} />

<div class="shell">
  <header>
    <div class="identity">
      <h1>WatchAlong</h1>
      {#if you !== null}
        <p class="whoami">
          You are <strong>{you.username}</strong>
          <span class="role">
            ({you.capability === 'host'
              ? 'host'
              : you.is_controller
                ? 'guest with control'
                : 'guest'})
          </span>
        </p>
      {/if}
    </div>

    <div class="header-actions">
      <p class="connection" data-phase={connection.phase} role="status">
        {connectionHeadline(connection)}
      </p>
      <button type="button" onclick={() => (renaming = !renaming)} aria-expanded={renaming}>
        Change name
      </button>
      <button type="button" onclick={() => (helpOpen = !helpOpen)} aria-expanded={helpOpen}>
        Shortcuts
      </button>
      <button type="button" onclick={onLeave}>Leave</button>
    </div>
  </header>

  {#if renaming}
    <form class="rename" onsubmit={submitRename}>
      <div class="field">
        <label for="username">Display name</label>
        <input
          bind:this={nameInput}
          id="username"
          type="text"
          autocomplete="nickname"
          maxlength={USERNAME_MAX_LENGTH}
          value={nameValue}
          oninput={(event) => (nameEdit = event.currentTarget.value)}
          aria-describedby="username-hint"
          aria-invalid={nameError !== null}
        />
        <p id="username-hint" class="hint">
          Remembered in this browser only. Other people can have the same name.
        </p>
      </div>
      <button type="submit" class="primary" disabled={room.isBusy('rename')}>Save name</button>
      {#if nameError !== null}<p class="alert" role="alert">{nameError}</p>{/if}
    </form>
  {/if}

  {#if helpOpen}
    <section class="shortcuts" aria-labelledby="shortcuts-heading">
      <h2 id="shortcuts-heading">Keyboard shortcuts</h2>
      <table>
        <thead>
          <tr><th scope="col">Keys</th><th scope="col">Action</th><th scope="col">Affects</th></tr>
        </thead>
        <tbody>
          {#each SHORTCUT_HELP as shortcut (shortcut.keys)}
            <tr>
              <td><kbd>{shortcut.keys}</kbd></td>
              <td>{shortcut.what}</td>
              <td>{shortcut.scope === 'room' ? 'everyone' : 'only you'}</td>
            </tr>
          {/each}
        </tbody>
      </table>
      <p class="hint">
        Shortcuts are ignored while you are typing in a text field. Shared actions follow the
        room's permissions.
      </p>
    </section>
  {/if}

  {#if connection.phase === 'session-lost'}
    <p class="alert" role="alert">
      {connection.message ?? 'Your session is no longer valid.'} Open your invite link again to
      re-join.
      <button type="button" class="dismiss" onclick={onLeave}>Re-join</button>
    </p>
  {:else if connection.phase === 'protocol-error'}
    <p class="alert" role="alert">{connection.message}</p>
  {:else if connection.phase === 'reconnecting'}
    <p class="warn" role="status">
      {connectionHeadline(connection)} Your player is paused; shared actions are unavailable until
      the room is back.
    </p>
  {/if}

  {#if room.notice !== null}
    {#key room.notice.id}
      <p
        class:alert={room.notice.severity === 'error'}
        class:warn={room.notice.severity === 'warning'}
        class:info={room.notice.severity === 'info'}
        role="alert"
      >
        {room.notice.text}
        <button type="button" class="dismiss" onclick={() => room.dismissNotice()}>Dismiss</button>
      </p>
    {/key}
  {/if}

  <div class="columns">
    <div class="main">
      <PlayerPane {room} />
      <ReactionBar {room} />
      <QueuePane {room} />
    </div>
    <aside>
      <ParticipantsPane {room} />
      <ChatPanel {room} />
      {#if permissions?.canAdminister === true}
        <SettingsPane {room} />
      {/if}
    </aside>
  </div>

  <footer>
    <p class="hint">
      Sponsorship segments come from
      <a href={SPONSORBLOCK_ATTRIBUTION.url} target="_blank" rel="noreferrer noopener">
        {SPONSORBLOCK_ATTRIBUTION.name}
      </a>. {SPONSORBLOCK_ATTRIBUTION.note} Skipping is always manual — nothing is skipped for you.
    </p>
  </footer>
</div>

<style>
  .shell {
    max-width: 82rem;
    margin: 0 auto;
    padding: 1rem 1.25rem 3rem;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .identity {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .whoami {
    margin: 0;
    color: var(--wa-text-dim);
  }

  .role {
    font-size: 0.85rem;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .connection {
    margin: 0;
    font-size: 0.85rem;
    color: var(--wa-text-dim);
    border: 1px solid var(--wa-border);
    border-radius: 999px;
    padding: 0.2rem 0.65rem;
  }

  .connection[data-phase='open'] {
    color: #9fe6b4;
    border-color: #2f6c45;
  }

  .connection[data-phase='reconnecting'],
  .connection[data-phase='connecting'] {
    color: var(--wa-warning);
    border-color: #6c5a2f;
  }

  .connection[data-phase='session-lost'],
  .connection[data-phase='protocol-error'],
  .connection[data-phase='closed'] {
    color: var(--wa-danger);
    border-color: #6c2f36;
  }

  .rename {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: var(--wa-surface);
    border: 1px solid var(--wa-border);
    border-radius: 0.6rem;
    padding: 0.9rem 1rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1 1 18rem;
  }

  .alert,
  .warn,
  .info {
    margin: 0;
    border-radius: 0.4rem;
    padding: 0.6rem 0.75rem;
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: space-between;
  }

  .alert {
    background: var(--wa-danger-surface);
    border: 1px solid #6c2f36;
    color: var(--wa-danger);
  }

  .warn {
    background: var(--wa-warning-surface);
    border: 1px solid #6c5a2f;
    color: var(--wa-warning);
  }

  .info {
    background: var(--wa-surface);
    border: 1px solid var(--wa-border);
    color: var(--wa-text);
  }

  .dismiss {
    flex-shrink: 0;
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
  }

  .columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20rem;
    gap: 0.9rem;
    align-items: start;
  }

  @media (width <= 62rem) {
    .columns {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  .main,
  aside {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    min-width: 0;
  }

  footer {
    border-top: 1px solid var(--wa-border);
    padding-top: 0.6rem;
  }

  footer .hint {
    margin: 0;
  }

  .shortcuts table {
    border-collapse: collapse;
    margin-top: 0.6rem;
    font-size: 0.9rem;
  }

  .shortcuts th,
  .shortcuts td {
    text-align: left;
    padding: 0.2rem 0.9rem 0.2rem 0;
  }

  .shortcuts th {
    color: var(--wa-text-dim);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  kbd {
    background: var(--wa-surface-raised);
    border: 1px solid var(--wa-border);
    border-radius: 0.25rem;
    padding: 0.05rem 0.35rem;
    font-family: inherit;
    font-size: 0.85rem;
  }
</style>
